import { useEffect, useState } from "react";
import { Col, FormGroup, Input, Label, Row, Table } from "reactstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { IoMdClose } from "react-icons/io";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO } from "date-fns";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import DefaultButton from "../../../../components/Buttons/Default";
import FixedSave from "../../../../components/Actions/FixedSave";
import CustomSelect from "../../../../components/Forms/CustomSelect";
import { get, post } from "../../../../utils/helpers/api";
import { useSettings } from "../../../../context/settings";

function emptyRow() {
  return {
    id: Date.now(),
    product_id: null,
    product_name: '',
    description: '',
    quantity_required: 1,
    unit_cost: 0
  };
}

function lineTotal(quantity, unitCost) {
  return parseFloat(quantity || 0) * parseFloat(unitCost || 0);
}

function toDate(value) {
  if (!value) return null;
  return typeof value === 'string' ? parseISO(value) : value;
}

function CreateProductionOrderPage() {
  const { t } = useTranslation();
  const { datePickerFormat } = useSettings();
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [saveLoading, setSaveLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [orderNumber, setOrderNumber] = useState(state?.productionOrderData?.order_number ?? '');
  const [billsOfMaterials, setBillsOfMaterials] = useState([]);

  const [orderData, setOrderData] = useState({
    product_id: state?.productionOrderData?.product_id ?? null,
    product_name: state?.productionOrderData?.product?.name ?? state?.productionOrderData?.product_name ?? '',
    bill_of_material_id: state?.productionOrderData?.bill_of_material_id ?? '',
    quantity_planned: state?.productionOrderData?.quantity_planned ?? 1,
    planned_start_date: toDate(state?.productionOrderData?.planned_start_date),
    planned_end_date: toDate(state?.productionOrderData?.planned_end_date),
    notes: state?.productionOrderData?.notes ?? ''
  });

  const [rows, setRows] = useState(
    state?.productionOrderData?.lines?.length > 0
      ? state.productionOrderData.lines.map((line, index) => ({
          id: Date.now() + index,
          product_id: line.product_id,
          product_name: line.product_name ?? '',
          description: line.description ?? '',
          quantity_required: line.quantity_required,
          unit_cost: line.unit_cost
        }))
      : [emptyRow()]
  );

  useEffect(() => {
    loadCreateData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCreateData = async () => {
    const data = await get('/production/orders/create');
    if (data?.success) {
      setBillsOfMaterials(data.bills_of_materials ?? []);
      if (!id) setOrderNumber(data.order_number);
    }
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setOrderData(prev => ({ ...prev, [name]: value }));
  };

  /**
   * Pull the scaled component list from the API rather than multiplying here, so
   * the requirement the user sees is the same number the server would compute.
   */
  const applyBillOfMaterials = async (billOfMaterialId, quantity) => {
    if (!billOfMaterialId) return;

    setPreviewLoading(true);
    const data = await get(`/production/orders/bom-preview/${billOfMaterialId}?quantity=${quantity || 1}`);
    if (data?.success) {
      setRows(
        data.lines.length > 0
          ? data.lines.map((line, index) => ({
              id: Date.now() + index,
              product_id: line.product_id,
              product_name: line.product_name ?? '',
              description: line.description ?? '',
              quantity_required: line.quantity_required,
              unit_cost: line.unit_cost
            }))
          : [emptyRow()]
      );
      toast.success(t('production.componentsLoadedFromBom'));
    }
    setPreviewLoading(false);
  };

  const onBillOfMaterialsChange = (e) => {
    const billOfMaterialId = e.target.value;
    const bom = billsOfMaterials.find(b => b.id === billOfMaterialId);

    setOrderData(prev => ({
      ...prev,
      bill_of_material_id: billOfMaterialId,
      // a recipe knows what it makes, so adopt its finished product
      product_id: bom?.product_id ?? prev.product_id,
      product_name: bom?.product?.name ?? prev.product_name
    }));

    applyBillOfMaterials(billOfMaterialId, orderData.quantity_planned);
  };

  const handleAddRow = () => setRows([...rows, emptyRow()]);
  const handleDeleteRow = (rowId) => setRows(rows.filter(row => row.id !== rowId));

  const updateRow = (rowId, changes) => {
    setRows(prev => prev.map(row => (row.id === rowId ? { ...row, ...changes } : row)));
  };

  const totalCost = rows.reduce((acc, row) => acc + lineTotal(row.quantity_required, row.unit_cost), 0);

  const submit = async () => {
    if (!orderData.product_id) return toast.error(t('production.productRequired'));
    if (!orderData.quantity_planned || Number(orderData.quantity_planned) < 1) return toast.error(t('production.quantityPlannedRequired'));

    const validRows = rows.filter(row => row.product_id && Number(row.quantity_required) >= 1);
    if (validRows.length === 0) return toast.error(t('production.atLeastOneComponent'));

    setSaveLoading(true);
    try {
      const url = id
        ? '/production/orders/update/' + id
        : '/production/orders/store';

      const data = await post(url, {
        ...orderData,
        bill_of_material_id: orderData.bill_of_material_id || null,
        quantity_planned: Number(orderData.quantity_planned),
        planned_start_date: orderData.planned_start_date ? format(orderData.planned_start_date, 'yyyy-MM-dd') : null,
        planned_end_date: orderData.planned_end_date ? format(orderData.planned_end_date, 'yyyy-MM-dd') : null,
        lines: validRows.map(row => ({
          product_id: row.product_id,
          product_name: row.product_name,
          description: row.description,
          quantity_required: Number(row.quantity_required),
          unit_cost: Number(row.unit_cost)
        }))
      });

      if (data?.success) {
        toast.success(t('production.productionOrderSaved'));
        navigate('/production/orders');
      } else {
        toast.error(data?.message || t('common.somethingWentWrong'));
      }
    } catch (err) {
      toast.error(err?.message || t('common.somethingWentWrong'));
    }
    setSaveLoading(false);
  };

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader
          breadcrumb={[
            t('sidebar.productionOrders'),
            id ? (orderNumber || t('common.edit')) : t('production.newProductionOrder')
          ]}
          onBack={() => navigate('/production/orders')}
        />

        <div className="subheader-page-content scrollable">
          <Row className="g-3 mb-3">
            <Col md="4">
              <Label>{t('sidebar.billsOfMaterials')}:</Label>
              <Input
                type="select"
                name="bill_of_material_id"
                value={orderData.bill_of_material_id ?? ''}
                onChange={onBillOfMaterialsChange}
              >
                <option value="">{t('production.noBillOfMaterials')}</option>
                {billsOfMaterials.map((bom) => (
                  <option key={bom.id} value={bom.id}>
                    {bom.reference} — {bom.product?.name ?? bom.name}
                  </option>
                ))}
              </Input>
            </Col>

            <Col md="4">
              <Label>{t('production.product')}: <span className="text-danger">*</span></Label>
              <CustomSelect
                module="inventory/products"
                value={orderData.product_id ? { value: orderData.product_id, label: orderData.product_name } : null}
                onChange={(selected) => setOrderData(prev => ({
                  ...prev,
                  product_id: selected ? selected.value : null,
                  product_name: selected ? selected.label : ''
                }))}
                placeholder={t('inventory.searchProduct')}
                createLink="/inventory/products/create"
              />
            </Col>

            <Col md="2">
              <Label>{t('production.quantityPlanned')}: <span className="text-danger">*</span></Label>
              <Input
                type="number"
                name="quantity_planned"
                min="1"
                step="1"
                value={orderData.quantity_planned}
                onChange={onChange}
                onBlur={() => applyBillOfMaterials(orderData.bill_of_material_id, orderData.quantity_planned)}
              />
            </Col>

            <Col md="2">
              <Label>{t('production.orderNumber')}:</Label>
              <Input value={orderNumber || t('production.generatedOnSave')} disabled />
            </Col>
          </Row>

          <Row className="g-3 mb-3">
            <Col md="3">
              <Label>{t('production.plannedStartDate')}:</Label>
              <DatePicker
                selected={orderData.planned_start_date}
                onChange={(d) => setOrderData(prev => ({ ...prev, planned_start_date: d }))}
                className="form-control"
                dateFormat={datePickerFormat}
                isClearable
              />
            </Col>
            <Col md="3">
              <Label>{t('production.plannedEndDate')}:</Label>
              <DatePicker
                selected={orderData.planned_end_date}
                onChange={(d) => setOrderData(prev => ({ ...prev, planned_end_date: d }))}
                className="form-control"
                dateFormat={datePickerFormat}
                isClearable
              />
            </Col>
          </Row>

          <p className="text-muted small mb-3">{t('production.componentsHint')}</p>

          <Table bordered className="prod-lines-table">
            <thead>
              <tr>
                <th>{t('production.component')}</th>
                <th>{t('common.description')}</th>
                <th width="120px">{t('production.quantityRequired')}</th>
                <th width="140px">{t('production.unitCost')}</th>
                <th width="140px">{t('common.total')}</th>
                <th width="40px"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((data) => (
                <tr key={data.id}>
                  <td style={{ minWidth: 220 }}>
                    <CustomSelect
                      module="inventory/products"
                      value={data.product_id ? { value: data.product_id, label: data.product_name } : null}
                      onChange={(selected, objectData) => updateRow(data.id, {
                        product_id: selected ? selected.value : null,
                        product_name: selected ? selected.label : '',
                        unit_cost: objectData?.cost_price ?? data.unit_cost
                      })}
                      placeholder={t('inventory.searchProduct')}
                      createLink="/inventory/products/create"
                    />
                  </td>
                  <td>
                    <Input
                      value={data.description}
                      onChange={(e) => updateRow(data.id, { description: e.target.value })}
                    />
                  </td>
                  <td>
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      value={data.quantity_required}
                      onChange={(e) => updateRow(data.id, { quantity_required: e.target.value })}
                    />
                  </td>
                  <td>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={data.unit_cost}
                      onChange={(e) => updateRow(data.id, { unit_cost: e.target.value })}
                    />
                  </td>
                  <td>
                    <Input value={lineTotal(data.quantity_required, data.unit_cost).toFixed(2)} disabled />
                  </td>
                  <td>
                    <IoMdClose className="prod-line-remove" onClick={() => handleDeleteRow(data.id)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <DefaultButton color="light" size="sm" className="mb-4" onClick={handleAddRow} disabled={previewLoading}>
            + {t('common.addNewRow')}
          </DefaultButton>

          <Row className="g-3 mb-3">
            <Col md="8">
              <FormGroup>
                <Label>{t('common.notes')}:</Label>
                <Input
                  type="textarea"
                  name="notes"
                  rows="4"
                  value={orderData.notes}
                  onChange={onChange}
                />
              </FormGroup>
            </Col>

            <Col md="4">
              <div className="d-flex justify-content-between">
                <span className="text-muted">{t('production.estimatedComponentCost')}</span>
                <strong>{totalCost.toFixed(2)}</strong>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      <FixedSave onSave={submit} loading={saveLoading} disabled={saveLoading || previewLoading} />
    </SidebarLayout>
  );
}

export default CreateProductionOrderPage;
